import { signIn, SignInAuthorizationParams, SignInOptions } from 'next-auth/react';
import { TreatmentWithConfig } from '@splitsoftware/splitio/types/splitio';
import { setSessionStorageItem } from 'src/services/storage/session';
import { DatadogRUMService } from 'src/services/datadog/DatadogRUMService';
import * as ExperimentService from 'src/services/experiments/ExperimentService.client';
import { FetchClient } from 'src/services/fetch/FetchService.client';
import { noop } from 'src/utils/noop';
import { areIntegrationTestsRunning } from 'src/utils/validators/areIntegrationTestsRunning';
import { getAuth0Scopes, ZILLOW_AUTH_PROVIDER } from 'src/services/auth/constants';
import { appendAuth0IdpIdentifier } from '../appendAuth0IdpIdentifier';
import { LOCATION_REQUESTED_SESSION_STORAGE_KEY, SUPPORTED_LOGIN_FLAG, ENABLE_OTP_LOGIN } from './constants';

export type ZillowSignInOptions = SignInOptions & {
    /**
     * When true, requests MFA-protected scopes (step-up authentication).
     * Used for accessing sensitive resources like pre-approval letters.
     * @default false
     */
    mfaRequired?: boolean;
    /**
     * When true, forces re-authentication even if user has an active session.
     * Used for MFA step-up from login page to ensure MFA prompt is shown.
     * Should be false for post-activation MFA where user just authenticated.
     * @default true (when mfaRequired is true)
     */
    forceReauth?: boolean;
};

/**
 * Determines if MFA should be enabled based on request, feature flag, and tests.
 * @param mfaRequested - Whether MFA was explicitly requested
 * @param experiments - Active experiments/feature flags
 * @returns Whether MFA scopes should be included
 */
const shouldIncludeMfaScopes = (
    mfaRequested: boolean,
    experiments: Record<ExperimentService.Domain.ActiveExperiment, TreatmentWithConfig | null>
): boolean => {
    // If MFA not requested, don't include MFA scopes
    if (!mfaRequested) {
        return false;
    }

    // Disable MFA for Cypress/integration tests even if requested
    if (areIntegrationTestsRunning()) {
        return false;
    }

    // MFA is enabled by default for security — only an explicit OFF disables it
    const mfaFeatureFlag = experiments[ExperimentService.Domain.ActiveExperiment.ZHL_HOMELOANS_DASHBOARD_MFA_FF];
    if (mfaFeatureFlag?.treatment === ExperimentService.Domain.FeatureFlagStatus.OFF) {
        return false;
    }

    return true;
};

export const zillowSignIn = async (
    experiments: Record<ExperimentService.Domain.ActiveExperiment, TreatmentWithConfig | null>,
    options?: ZillowSignInOptions,
    authorizationParams?: Record<string, string>
) => {
    const fetchClient = FetchClient.getInstance();
    await DatadogRUMService.Client.getInstance().addAction(
        DatadogRUMService.CustomAction.AUTHENTICATION_START,
        undefined,
        {
            onlyLogOnce: true,
        }
    );
    const provider = ZILLOW_AUTH_PROVIDER.AUTH0;
    setSessionStorageItem(LOCATION_REQUESTED_SESSION_STORAGE_KEY, globalThis.location.pathname);

    // Determine scopes - MFA scopes only included when explicitly requested and allowed
    const { mfaRequired = false, forceReauth, ...signInOptions } = options ?? {};
    const includeMfaScopes = shouldIncludeMfaScopes(mfaRequired, experiments);
    const scopes = getAuth0Scopes(includeMfaScopes);

    // Determine if we should force re-authentication
    // - For MFA step-up from login page (returning users): force re-auth to ensure MFA prompt
    // - For post-activation MFA (user just authenticated): don't force re-auth, just request MFA scope
    // Default: force re-auth when MFA is required (unless explicitly set to false)
    const shouldForceReauth = includeMfaScopes && (forceReauth ?? true);

    const authParams: SignInAuthorizationParams = {
        [SUPPORTED_LOGIN_FLAG]: ENABLE_OTP_LOGIN,
        ...authorizationParams,
        scope: scopes.join(' '),
        // Only force re-authentication when explicitly needed (e.g., returning users on login page)
        // For post-activation MFA, we don't want to show login screen again
        ...(shouldForceReauth && { prompt: 'login' }),
    };
    await fetchClient
        .fetch('/homeloans/mortgage/api/auth/metrics/signin', {
            method: 'POST',
            body: {
                location: globalThis.location.pathname,
            },
        })
        .catch(noop);
    await appendAuth0IdpIdentifier(authParams);
    setTimeout(() => {
        // defer sign-in long enough for the dispatch to propagate
        signIn(provider, signInOptions, authParams);
    }, 250);
};
